import { useEffect, useState } from "react";
import type { SpeakMode, VoiceProvider } from "@/lib/speech/voiceSettings";
import {
  DEFAULT_VOICE_VOLUME,
  getVoiceVolume,
  setVoiceVolume,
  subscribeVoiceSettings,
} from "@/lib/speech/voiceSettings";
import { speak } from "@/lib/speech/speak";
import { SPEAK_OPTIONS } from "./settingsDrawerConstants";

export function SettingsDrawerVoiceSection(props: {
  voiceEnabled: boolean;
  setVoiceEnabled: (v: boolean) => void;
  voiceProvider: VoiceProvider;
  setVoiceProvider: (v: VoiceProvider) => void;
  speakMode: SpeakMode;
  setSpeakMode: (m: SpeakMode) => void;
}) {
  const { voiceEnabled, setVoiceEnabled, voiceProvider, setVoiceProvider, speakMode, setSpeakMode } = props;

  // Per-user playback volume preference — fixes dev-sync note f7e2c436 where
  // the lead-finalize coach voice was too loud in front of customers. Slider
  // writes through to localStorage; `speak()` reads `getVoiceVolume()` on
  // every call so this lands instantly without needing context plumbing.
  const [volume, setVolumeState] = useState<number>(() => {
    try {
      return getVoiceVolume();
    } catch {
      return DEFAULT_VOICE_VOLUME;
    }
  });
  useEffect(() => {
    return subscribeVoiceSettings((snap) => setVolumeState(snap.volume));
  }, []);
  const volumePct = Math.round(volume * 100);
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Voice</h3>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">Voice output</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Spoken audio feedback</div>
        </div>
        <button
          type="button"
          onClick={() => setVoiceEnabled(!voiceEnabled)}
          className={
            "relative inline-flex h-6 w-11 items-center rounded-full transition-colors " +
            (voiceEnabled ? "bg-indigo-600" : "bg-gray-300 dark:bg-gray-600")
          }
          role="switch"
          aria-checked={voiceEnabled}
        >
          <span
            className={
              "inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform " +
              (voiceEnabled ? "translate-x-6" : "translate-x-1")
            }
          />
        </button>
      </div>

      <div className={voiceEnabled ? "" : "opacity-40 pointer-events-none"}>
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs text-gray-500 dark:text-gray-400">Volume</p>
            <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{volumePct}%</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={volumePct}
              onChange={(e) => {
                const next = Number(e.target.value) / 100;
                setVolumeState(next);
                setVoiceVolume(next);
              }}
              aria-label="Voice playback volume"
              className="flex-1 accent-indigo-600"
            />
            <button
              type="button"
              onClick={() => speak("This is how the voice sounds at the current volume.")}
              className="text-[11px] font-medium text-indigo-700 hover:underline dark:text-indigo-400"
              aria-label="Test current voice volume"
            >
              Test
            </button>
          </div>
          <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 leading-snug">
            Lower this when a customer is sitting beside the screen — applies to the lead-finalize coach and every
            other spoken prompt.
          </p>
        </div>

        <div className="mb-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Quality</p>
          <div className="flex gap-2">
            {(["system", "custom"] as VoiceProvider[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setVoiceProvider(p)}
                className={
                  "flex-1 py-1.5 rounded-lg border text-xs transition-colors " +
                  (voiceProvider === p
                    ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 font-medium"
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700")
                }
              >
                {p === "system" ? "System" : "Custom"}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Speak</p>
          <div className="space-y-1.5">
            {SPEAK_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSpeakMode(opt.value)}
                className={
                  "w-full text-left px-3 py-2 rounded-xl border text-xs transition-colors " +
                  (speakMode === opt.value
                    ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300"
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700")
                }
              >
                <span className="font-medium">{opt.label}</span>
                <span className="ml-2 text-gray-500 dark:text-gray-400">{opt.description}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
